/**
 * Intervenant portrait with a guaranteed fallback.
 *
 * When `photoUrl` is provided the real photo is shown (served same-origin by
 * /api/intervenants/{id}/photo — the CSP blocks external images on purpose).
 * Without a photo, or if it fails to load, deterministic initials on a stable
 * hue per name keep every person recognisable — no network dependency.
 */
import { useState } from 'react';

export function Avatar({
  name,
  size = 36,
  photoUrl,
  className = '',
}: {
  name: string;
  size?: number;
  photoUrl?: string;
  /** Extra classes (the size variant of an avatar placed before a name). */
  className?: string;
}) {
  // The URL that failed, not a flag: a new photo (re-upload, another person
  // in the same slot) is tried again instead of staying on the initials.
  const [failedUrl, setFailedUrl] = useState('');
  const cleaned = String(name || '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const initials = parts.length === 0
    ? '?'
    : parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  let hash = 0;
  for (let index = 0; index < cleaned.length; index += 1) {
    hash = (hash * 31 + cleaned.charCodeAt(index)) >>> 0;
  }
  const hue = hash % 360;
  const extra = className ? ` ${className}` : '';
  if (photoUrl && failedUrl !== photoUrl) {
    return (
      <img
        className={`ui-avatar ui-avatar--photo${extra}`}
        src={photoUrl}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        draggable={false}
        style={{ width: size, height: size }}
        onError={() => setFailedUrl(photoUrl)}
      />
    );
  }
  return (
    <span
      className={`ui-avatar${extra}`}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(size < 24 ? 8 : 10, Math.round(size * 0.4)),
        background: `hsl(${hue} 55% 42%)`,
      }}
    >
      {initials}
    </span>
  );
}
